import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { forgetToastAction, runToastAction } from "../features/toast/toastActions";
import { dismissToast, ToastMessage } from "../features/toast/toastSlice";

export const AUTO_DISMISS_MS = 3000;
/** A toast with a button ("Undo") stays long enough to reach the button. */
export const ACTION_DISMISS_MS = 8000;

const ToastCard = ({ toast }: { toast: ToastMessage }) => {
    const dispatch = useAppDispatch();
    // Paused while the pointer or keyboard focus is on it (WCAG 2.2.1: time limits can be extended).
    const [paused, setPaused] = useState(false);

    // Closing takes the waiting callback with it. Done here, when it is dismissed, and not in an
    // unmount clean-up: React's StrictMode unmounts and remounts once in development, which ran
    // that clean-up the moment the toast appeared and left an Undo button that did nothing.
    const close = useCallback(() => {
        if (toast.action) forgetToastAction(toast.action.id);
        dispatch(dismissToast(toast.id));
    }, [toast.id, toast.action, dispatch]);

    useEffect(() => {
        if (paused) return undefined;
        const timer = window.setTimeout(close, toast.action ? ACTION_DISMISS_MS : AUTO_DISMISS_MS);
        return () => window.clearTimeout(timer);
    }, [toast.action, paused, close]);

    return (
        <div className={`toast toast--${toast.variant}`} role={toast.variant === "error" ? "alert" : "status"}
            onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
            <span className="toast__icon" aria-hidden="true">
                {toast.variant === "success" ? "✓" : "✕"}
            </span>
            <p className="toast__message">{toast.message}</p>
            {toast.action && (
                <button type="button" className="btn btn-ghost btn-compact toast__action"
                    onClick={() => { runToastAction(toast.action!.id); close(); }}>
                    {toast.action.label}
                </button>
            )}
            <button
                type="button"
                className="toast__close"
                aria-label="Dismiss notification"
                onClick={close}
            >
                ×
            </button>
        </div>
    );
};

/**
 * Single global mount (see AppProviders) for every create/update/delete result
 * across the app. Floats above content instead of the old inline banners that
 * rendered wherever their owning form happened to sit in the page flow.
 */
export const ToastHost = () => {
    const items = useAppSelector((state) => state.toast.items);

    // The live region is always there, empty or not: a region created together with its
    // first message is often not announced by screen readers (WCAG 4.1.3).
    if (typeof document === "undefined") {
        return null;
    }

    return createPortal(
        <div className="toast-stack" aria-live="polite" aria-atomic="false">
            {items.map((toast) => (
                <ToastCard key={toast.id} toast={toast} />
            ))}
        </div>,
        document.body
    );
};
